import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { Button } from "@/core/components/ui/button";
import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { useMerchantSales } from "@/features/merchant/api/use-merchant-sales";
import type { MerchantSaleFilter } from "@/features/merchant/api/dto";
import {
  merchantSalesColumns,
  type MerchantSaleRow,
} from "@/features/merchant/components/merchant-sales/merchant-sales-columns";
import { SimulateSaleDialog } from "@/features/merchant/components/merchant-sales/simulate-sale-dialog";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/**
 * Section « Ventes » d'un marchand — encapsule le filtre `merchantId` puis
 * délègue à `POST /MerchantSales/all` (pagination server).
 */
export function MerchantSalesSection({ merchantId }: { merchantId?: string }) {
  const { t: tm } = useMerchantTranslation();
  const [simulateOpen, setSimulateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const filter: MerchantSaleFilter = useMemo(() => {
    const base: MerchantSaleFilter = merchantId ? { merchantId } : {};
    for (const { id, value } of columnFilters) {
      if (typeof value !== "string" || !value) continue;
      if (id === "status") base.status = value as MerchantSaleFilter["status"];
      if (id === "salesAgentId") base.salesAgentId = value;
    }
    return base;
  }, [merchantId, columnFilters]);

  const params = useMemo(
    () => ({
      filter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [filter, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useMerchantSales(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.sales.title")}
        </h2>
        <RequireRole
          roles={[
            FLASHPAY_ROLES.SERVICE,
            FLASHPAY_ROLES.SUPERADMIN,
            FLASHPAY_ROLES.ADMIN,
          ]}
        >
          <Button variant="outline" onClick={() => setSimulateOpen(true)}>
            {tm("merchant.sales.simulate.submit")}
          </Button>
        </RequireRole>
      </div>

      <SimulateSaleDialog
        open={simulateOpen}
        onOpenChange={setSimulateOpen}
        merchantId={merchantId}
      />

      <DataTableClar
        data={(data?.items ?? []).filter(
          (s): s is MerchantSaleRow => Boolean(s.id)
        )}
        columnsClar={merchantSalesColumns}
        title={tm("merchant.sales.title")}
        exportFileName="merchant-sales"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />
    </section>
  );
}
